import { useState, useCallback } from 'react';

import { useIntl } from 'react-intl';

import { PricingTabs } from '@/components/wpl/pricing/src/tabs';

import { ErrorMessage } from '../../../../uniform/components/pricing-calculator/src/components/error-message.tsx';
import { LoadingSpinner } from '../../../../uniform/components/pricing-calculator/src/components/spinner';
import { GetDiscountedPriceArgs } from '../../../../uniform/components/pricing-calculator/src/context.tsx';
import UniformPricingCalculator from '../../../../uniform/components/pricing-calculator/src/price-calculator';

import { pricingTitleInternal } from './client.ts';
import { useNonProfitPricingCalculatorData } from './hooks';

import type {
	BillingCycle,
	InputValues,
} from '../../../../uniform/components/pricing-calculator/src/types';

const DEFAULT_UNIT_COUNT = 25;
const DEFAULT_BILLING_CYCLE: BillingCycle = 'ANNUAL';
const FULL_DISCOUNT_MAX_UNITS = 25;

export const NonProfitPricingCalculator = () => {
	const { plansDataByProduct, error } = useNonProfitPricingCalculatorData();

	const [inputs, setInputs] = useState<InputValues>({
		unitCount: DEFAULT_UNIT_COUNT,
		billingCycle: DEFAULT_BILLING_CYCLE,
	});

	const getDiscountedPrice = useCallback((args: GetDiscountedPriceArgs) => {
		const { price, inputs } = args;
		if (inputs.billingCycle === 'ANNUAL' && inputs.unitCount <= FULL_DISCOUNT_MAX_UNITS) {
			return 0;
		}
		return price * 0.25;
	}, []);

	const intl = useIntl();

	if (error) {
		return <ErrorMessage />;
	}

	if (!plansDataByProduct) {
		return <LoadingSpinner />;
	}

	const fullDiscountBanner = intl.formatMessage({
		id: 'nonprofit.fullDiscountBanner',
		defaultMessage: 'Showing 100% off',
	});

	const partialDiscountBanner = intl.formatMessage({
		id: 'nonprofit.partialDiscountBanner',
		defaultMessage: 'Showing 75% off',
	});

	const partialDiscountWithPromptBanner = intl.formatMessage({
		id: 'nonprofit.partialDiscountWithPromptBanner',
		defaultMessage: 'Showing 75% off - choose Annual billing for 100% off',
	});

	let banner = partialDiscountBanner;
	if (inputs.billingCycle === 'ANNUAL' && inputs.unitCount <= FULL_DISCOUNT_MAX_UNITS) {
		banner = fullDiscountBanner;
	}
	if (inputs.billingCycle === 'MONTHLY') {
		banner = partialDiscountWithPromptBanner;
	}

	return (
		<PricingTabs
			banner={banner}
			jiraCalculatorComponent={
				<UniformPricingCalculator
					comparePlansData={plansDataByProduct[pricingTitleInternal.JIRA] ?? []}
					urlSlug="jira"
					inputs={inputs}
					onChangeInputs={setInputs}
					getDiscountedPrice={getDiscountedPrice}
				/>
			}
			confluenceCalculatorComponent={
				<UniformPricingCalculator
					comparePlansData={plansDataByProduct[pricingTitleInternal.CONFLUENCE] ?? []}
					urlSlug="confluence"
					inputs={inputs}
					onChangeInputs={setInputs}
					getDiscountedPrice={getDiscountedPrice}
				/>
			}
			teamworkCollectionComponent={
				<UniformPricingCalculator
					comparePlansData={plansDataByProduct[pricingTitleInternal.TWC] ?? []}
					urlSlug="teamwork"
					inputs={inputs}
					onChangeInputs={setInputs}
					getDiscountedPrice={getDiscountedPrice}
				/>
			}
			loomCalculatorComponent={
				<UniformPricingCalculator
					comparePlansData={plansDataByProduct[pricingTitleInternal.LOOM] ?? []}
					urlSlug="loom"
					inputs={inputs}
					onChangeInputs={setInputs}
					getDiscountedPrice={getDiscountedPrice}
				/>
			}
		/>
	);
};
